import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const SITE_NAME = 'Kolkata Electronics';
export const SITE_URL = (import.meta.env.VITE_SITE_URL || 'https://kolkataelectronics.com').replace(/\/$/, '');
export const DEFAULT_DESCRIPTION = 'Buy genuine mobiles, laptops, audio and electronics at smart local prices with clear warranty and dependable Kolkata support.';

export const absoluteUrl = (path = '/') => {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

export const breadcrumbSchema = (items) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    ...(item.path ? { item: absoluteUrl(item.path) } : {}),
  })),
});

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
};

const upsertLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = '/brand/social-card.png',
  type = 'website',
  noIndex = false,
  schema = [],
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Right Tech. Right Price. Right Here.`;
    const canonical = absoluteUrl(path);
    const socialImage = absoluteUrl(image);
    const robots = noIndex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large';

    document.title = fullTitle;
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
    upsertMeta('meta[name="googlebot"]', { name: 'googlebot', content: robots });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: socialImage });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_IN' });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: socialImage });
    upsertLink('canonical', canonical);

    let jsonLd = document.head.querySelector('#seo-json-ld');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.id = 'seo-json-ld';
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }

    const graph = (Array.isArray(schema) ? schema : [schema]).filter(Boolean).map(({ '@context': _context, ...item }) => item);
    jsonLd.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });

    return () => {
      document.head.querySelector('#seo-json-ld')?.remove();
    };
  }, [title, description, path, image, type, noIndex, schema]);

  return null;
}

export function NoIndexRoute({ title, children }) {
  const { pathname } = useLocation();
  return (
    <>
      <Seo title={title} path={pathname} noIndex />
      {children}
    </>
  );
}
